/**
 * CORE — deterministic sphere-vs-box collision primitives.
 *
 * Pure-JS, double-precision geometry queries used by the physics integrator.
 * No Rapier, no platform globals — so the result is bit-identical everywhere.
 *
 * The marble is a sphere; every solid course element is an oriented box (OBB)
 * described by a center, a unit quaternion, and half-extents. We resolve the
 * classic "closest point on OBB to sphere center" test, which gives both the
 * penetration depth and the contact normal needed for response.
 *
 * Owned by: Core. Internal helper.
 */

import type { Vec3, Quat } from '@/types';
import { quatRotate, quatConjugate, sub, add, scale, dot } from './vecmath';

export interface Contact {
  /** World-space outward normal pointing from the box toward the sphere. */
  readonly normal: Vec3;
  /** Positive penetration depth (how far the sphere overlaps the box). */
  readonly depth: number;
  /** World-space closest point on the box surface. */
  readonly point: Vec3;
}

/**
 * Closest point test between a sphere (center, radius) and an oriented box.
 * Returns null when there is no overlap. Deterministic and allocation-light.
 */
export function sphereVsOBB(
  sphereCenter: Vec3,
  radius: number,
  boxCenter: Vec3,
  boxRotation: Quat,
  halfExtents: Vec3,
): Contact | null {
  // Transform the sphere center into the box's local frame.
  const invRot = quatConjugate(boxRotation);
  const rel = sub(sphereCenter, boxCenter);
  const local = quatRotate(invRot, rel);

  // Closest point on the box (in local space) to the sphere center.
  const cx = clampAbs(local[0], halfExtents[0]);
  const cy = clampAbs(local[1], halfExtents[1]);
  const cz = clampAbs(local[2], halfExtents[2]);
  const closestLocal: Vec3 = [cx, cy, cz];

  // Vector from closest point to sphere center (local space).
  const dx = local[0] - cx;
  const dy = local[1] - cy;
  const dz = local[2] - cz;
  const distSq = dx * dx + dy * dy + dz * dz;

  if (distSq > radius * radius) return null;

  let normalLocal: Vec3;
  let depth: number;
  if (distSq > 1e-12) {
    const dist = Math.sqrt(distSq);
    normalLocal = [dx / dist, dy / dist, dz / dist];
    depth = radius - dist;
  } else {
    // Sphere center is inside the box: push out along the least-penetrated axis.
    const px = halfExtents[0] - Math.abs(local[0]);
    const py = halfExtents[1] - Math.abs(local[1]);
    const pz = halfExtents[2] - Math.abs(local[2]);
    if (px <= py && px <= pz) {
      normalLocal = [local[0] < 0 ? -1 : 1, 0, 0];
      depth = radius + px;
    } else if (py <= pz) {
      normalLocal = [0, local[1] < 0 ? -1 : 1, 0];
      depth = radius + py;
    } else {
      normalLocal = [0, 0, local[2] < 0 ? -1 : 1];
      depth = radius + pz;
    }
  }

  const normal = quatRotate(boxRotation, normalLocal);
  const closestWorld = add(boxCenter, quatRotate(boxRotation, closestLocal));
  return { normal, depth, point: closestWorld };
}

/**
 * Cheap overlap test (no normal): is the sphere center within `radius` of the
 * oriented box? Used for trigger volumes (hazards, boosters) where we only need
 * a yes/no and don't resolve a contact.
 */
export function sphereOverlapsOBB(
  sphereCenter: Vec3,
  radius: number,
  boxCenter: Vec3,
  boxRotation: Quat,
  halfExtents: Vec3,
): boolean {
  const invRot = quatConjugate(boxRotation);
  const local = quatRotate(invRot, sub(sphereCenter, boxCenter));
  const dx = local[0] - clampAbs(local[0], halfExtents[0]);
  const dy = local[1] - clampAbs(local[1], halfExtents[1]);
  const dz = local[2] - clampAbs(local[2], halfExtents[2]);
  return dx * dx + dy * dy + dz * dz <= radius * radius;
}

/** Sphere-vs-sphere overlap (checkpoints are spheres). */
export function sphereOverlapsSphere(
  a: Vec3,
  ar: number,
  b: Vec3,
  br: number,
): boolean {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  const r = ar + br;
  return dx * dx + dy * dy + dz * dz <= r * r;
}

function clampAbs(value: number, limit: number): number {
  return value < -limit ? -limit : value > limit ? limit : value;
}

/** Resolve a velocity against a contact normal: cancel the inward (normal)
 *  component and apply restitution as a bounce. Tangential velocity is PRESERVED
 *  — a rolling marble keeps its speed along the surface. Ground rolling-resistance
 *  is applied once per tick by the integrator (PhysicsWorld.step), not scrubbed on
 *  every contact (which would glue the marble to the floor). Deterministic. */
export function resolveVelocity(
  velocity: Vec3,
  normal: Vec3,
  restitution: number,
): Vec3 {
  const vn = dot(velocity, normal);
  if (vn >= 0) return velocity; // separating; nothing to do.
  const normalVel = scale(normal, vn);
  const tangentVel = sub(velocity, normalVel);
  const bounced = scale(normalVel, -restitution);
  return add(bounced, tangentVel);
}
